"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  HighlightedText,
  type HighlightTone,
  type TextMatch,
} from "@/components/tool-kit/highlighted-text";
import {
  Checkbox,
  DataTable,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  CheckerInput,
  ToneLegend,
} from "@/components/tools/style-checkers-a/a-frame";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import {
  checkDialoguePunctuation,
  type DialogueFinding,
  type DialogueRule,
} from "@/lib/tool-kit/style-checkers/dialogue-punctuation";

/** English strings. A translated content file overrides these through `ui`. */
export const DIALOGUE_PUNCTUATION_CHECKER_STRINGS = {
  textLabel: "Your dialogue",
  placeholder:
    "Paste a scene or a chapter. Start a new line for each paragraph…",
  privacy: "Runs entirely in your browser — your text is never uploaded.",
  tryExample: "Try an example",
  clear: "Clear",
  exampleText:
    "“I’m leaving.” she said.\nHe asked “Are you sure?”\n“Stop!” He shouted.\n“I’ll be there,” she grabbed her coat.\n“We should go,” Maria said, “When the rain stops.”\n“It was a mistake”, he said.\n“Are you coming?”, she asked.\n“I know,” the old man said.\n“Wait,” said Tom. “I forgot my keys.”\nShe said, “i’m tired.”",
  checkPlacement: "Check periods and commas inside the quote marks (American)",
  enterPrompt:
    "Paste or type some dialogue to check its commas, periods, capital letters and quotation marks.",
  results: "Results",
  toCheck: "To check",
  firm: "Rule breaks",
  judgement: "Judgement calls",
  quotes: "Quotations read",
  lines: "Lines",
  words: "Words",
  highlightedHeading: "Your text, highlighted",
  highlightLabel: "Your dialogue with punctuation problems highlighted",
  legendFirm: "Rule break",
  legendJudgement: "Judgement call",
  hoverHint:
    "Hover over a highlight to see the rule; the table below gives the corrected wording for each.",
  noneFound: "No dialogue punctuation problems found.",
  noQuotes:
    "No double quotation marks found. Single-quote dialogue is not read.",
  findingsHeading: "What to change",
  colLine: "Line",
  colRule: "Rule",
  colWritten: "As written",
  colFixed: "Change to",
  decide: "Your call",
  rulePeriodBeforeTag:
    "A quote that a tag follows ends with a comma, not a period: “I’m leaving,” she said.",
  ruleMissingComma:
    "A tag that comes before the quote takes a comma: She said, “I’m leaving.”",
  ruleCommaAfterQuestion:
    "No comma after a question mark or exclamation point in dialogue: “Are you leaving?” she asked.",
  ruleMarkPlacement:
    "In American style periods and commas go inside the closing quotation mark.",
  ruleCapitalTag:
    "After a comma, question mark or exclamation point the tag stays lowercase: “Stop!” he shouted.",
  ruleActionBeat:
    "An action is not a tag. End the quote with a period and start a new sentence, or use a speech verb such as said.",
  ruleLowercaseStart:
    "Speech that opens a sentence starts with a capital letter.",
  ruleCapitalContinuation:
    "The second half of a quote broken by a tag continues the sentence, so it starts lowercase.",
  ruleUnclosed:
    "This opening quotation mark has no closing mark on its line, and the next line does not open with one. Close it, or open the next paragraph with a quotation mark if the speech runs on.",
  note: "Reads double quotation marks, straight or curly, one line at a time. American conventions; British writers who put punctuation outside the marks can untick the placement check.",
} as const;

type Strings = typeof DIALOGUE_PUNCTUATION_CHECKER_STRINGS;

const RULE_TEXT: Record<DialogueRule, keyof Strings> = {
  "period-before-tag": "rulePeriodBeforeTag",
  "missing-comma-after-tag": "ruleMissingComma",
  "comma-after-question": "ruleCommaAfterQuestion",
  "mark-placement": "ruleMarkPlacement",
  "capital-tag": "ruleCapitalTag",
  "action-beat": "ruleActionBeat",
  "lowercase-start": "ruleLowercaseStart",
  "capital-continuation": "ruleCapitalContinuation",
  unclosed: "ruleUnclosed",
};

const TONES: Record<string, HighlightTone> = {
  firm: "rose",
  judgement: "amber",
};

function ruleText(finding: DialogueFinding, strings: Strings): string {
  return strings[RULE_TEXT[finding.rule]];
}

export default function DialoguePunctuationCheckerWidget() {
  const strings = useToolUi(DIALOGUE_PUNCTUATION_CHECKER_STRINGS);
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [checkPlacement, setCheckPlacement] = useState(true);
  const deferred = useDeferredValue(text);
  const report = useMemo(
    () => checkDialoguePunctuation(deferred, { checkPlacement }),
    [deferred, checkPlacement]
  );
  const firm = report.findings.filter((finding) => finding.firm).length;
  const highlights: TextMatch[] = report.findings.map((finding) => ({
    start: finding.start,
    end: finding.end,
    category: finding.firm ? "firm" : "judgement",
    title: ruleText(finding, strings),
  }));
  const hasText = deferred.trim().length > 0;

  return (
    <div className="space-y-6">
      <CheckerInput
        clearLabel={strings.clear}
        example={strings.exampleText}
        exampleLabel={strings.tryExample}
        extra={
          <Checkbox
            checked={checkPlacement}
            label={strings.checkPlacement}
            onChange={setCheckPlacement}
          />
        }
        hint={strings.privacy}
        id="dpc-text"
        label={strings.textLabel}
        onChange={setText}
        placeholder={strings.placeholder}
        value={text}
      />

      {hasText ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite" className="space-y-4">
            <StatGrid>
              <StatCard
                emphasis
                label={strings.toCheck}
                value={fmt(report.findings.length)}
              />
              <StatCard label={strings.firm} value={fmt(firm)} />
              <StatCard
                label={strings.judgement}
                value={fmt(report.findings.length - firm)}
              />
              <StatCard label={strings.quotes} value={fmt(report.quotes)} />
              <StatCard label={strings.lines} value={fmt(report.lines)} />
              <StatCard label={strings.words} value={fmt(report.words)} />
            </StatGrid>
            <p className={HINT}>{strings.note}</p>
          </div>

          <div className="space-y-3">
            <h3 className={WIDGET_H3}>{strings.highlightedHeading}</h3>
            <ToneLegend
              items={[
                { tone: "rose", label: strings.legendFirm },
                { tone: "amber", label: strings.legendJudgement },
              ]}
            />
            <HighlightedText
              label={strings.highlightLabel}
              matches={highlights}
              text={deferred}
              tones={TONES}
            />
            <p className={HINT}>{strings.hoverHint}</p>
          </div>

          {report.quotes === 0 && report.findings.length === 0 ? (
            <Notice>{strings.noQuotes}</Notice>
          ) : null}
          {report.quotes > 0 && report.findings.length === 0 ? (
            <Notice>{strings.noneFound}</Notice>
          ) : null}

          {report.findings.length > 0 ? (
            <div className="space-y-3">
              <h3 className={WIDGET_H3}>{strings.findingsHeading}</h3>
              <DataTable
                head={[
                  strings.colLine,
                  strings.colRule,
                  strings.colWritten,
                  strings.colFixed,
                ]}
                rows={report.findings.map((finding) => [
                  fmt(finding.line),
                  ruleText(finding, strings),
                  finding.written,
                  finding.fixed ?? strings.decide,
                ])}
              />
            </div>
          ) : null}
        </section>
      ) : (
        <Notice>{strings.enterPrompt}</Notice>
      )}
    </div>
  );
}
